import { Injectable, inject } from '@angular/core';
import { Params, Router } from '@angular/router';

import { PrettyUrlsConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { SportsProductService } from '@frontend/sports/host-app/sports-product/feature/sports-product-service';
import { Page, PageViewDataProvider, TrackingData } from '@frontend/vanilla/core';
import { kebabCase } from 'lodash-es';
import { Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PrettyUrlPageViewDataProvider implements PageViewDataProvider {
    private readonly page = inject(Page);
    private readonly sportsProductService = inject(SportsProductService);
    private readonly router = inject(Router);
    private readonly urlConfig = inject(PrettyUrlsConfig);

    private previousPage: string | null = null;

    getData(): Observable<TrackingData> {
        const pageName = this.getPageName();

        const data = {
            'page.name': pageName,
            'page.referrer': this.previousPage,
        };

        this.previousPage = pageName;

        return of(data);
    }

    getPageName(): string {
        const separator = '/';

        let node = this.router.routerState.snapshot.root;
        let segments: string[] = this.getDefaultSegments();
        let params: Params = {};
        let path = '';

        while (node) {
            segments.push(node.data['originalPath']);
            params = { ...node.params };

            node = node.children[0];
        }

        segments = segments
            .filter(isDefined)
            .filter((current) => current !== '')
            .filter((current, index) => index > 0);
        path = segments.join(separator);

        // fix for context parameter for sport pages and tab parameter for my bets
        params = this.setToken(params, 'context');
        params = this.setToken(params, 'tab');

        for (const [key, value] of Object.entries(params)) {
            path = path.replace(`:${key}`, value);
        }

        return path;
    }

    private setToken(params: Params, key: string): Params {
        if (!params[key]) {
            return params;
        }

        const param = Object.entries(this.urlConfig.translations).find(([_, value]) => value === params[key])?.[0];

        if (!param) {
            return params;
        }

        return {
            ...params,
            [key]: kebabCase(param),
        };
    }

    private getDefaultSegments(): string[] {
        // In Single Domain these segments (/{culture}/sports) don't get processed by our sports processors as they are in host-app.
        // Therefore we need to add these segments manually here.
        return this.sportsProductService.isSingleDomain ? [this.page.culture, this.sportsProductService.sportsProductName] : [];
    }
}
